/**
 * Check-in kiosk / station view.
 *
 * A focused, touch-friendly station screen with two modes:
 *   - Check in: pick the occurrence, search the directory, and check a person in.
 *     On success the issued single-use security code + printable label payloads
 *     are surfaced so the volunteer can hand the guardian their pickup tag.
 *   - Check out: a guardian presents the code; the operator selects who is
 *     collecting. The server enforces the guardian/household check (child safety)
 *     and an audited override path for exceptions.
 */
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Spinner } from '@fc/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  LogIn,
  LogOut,
  Printer,
  Search,
  ShieldCheck,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { listEvents, listOccurrences, type EventOccurrenceDto } from '../../lib/events-api'
import { listPeople, type Person } from '../../lib/api'
import { checkIn, checkOut, type CheckInResult } from '../../lib/checkins-api'

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// ----------------------------- occurrence picker -----------------------------

function OccurrencePicker({
  value,
  onChange,
}: {
  value: string | null
  onChange: (occurrenceId: string) => void
}) {
  const eventsQuery = useQuery({ queryKey: ['checkin-events'], queryFn: listEvents })
  const checkinEvents = (eventsQuery.data ?? []).filter((e) => e.checkinEnabled)
  const [eventId, setEventId] = useState<string>('')

  const occQuery = useQuery({
    queryKey: ['checkin-occurrences', eventId],
    queryFn: () => listOccurrences(eventId),
    enabled: !!eventId,
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Session</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted">Event</label>
          <select
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            className="h-10 w-full rounded-md border border-border bg-bg-alt px-3 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none"
          >
            <option value="">Select an event…</option>
            {checkinEvents.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          {eventsQuery.data && checkinEvents.length === 0 && (
            <p className="mt-1 text-xs text-dim">No events have check-in enabled yet.</p>
          )}
        </div>
        {eventId && (
          <div>
            <label className="mb-1 block text-xs font-medium text-muted">Occurrence</label>
            {occQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted">
                <Spinner /> Loading…
              </div>
            ) : (
              <select
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                className="h-10 w-full rounded-md border border-border bg-bg-alt px-3 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none"
              >
                <option value="">Select a session time…</option>
                {(occQuery.data ?? [])
                  .filter((o: EventOccurrenceDto) => o.status === 'scheduled')
                  .map((o: EventOccurrenceDto) => (
                    <option key={o.id} value={o.id}>
                      {formatDateTime(o.startsAt)}
                    </option>
                  ))}
              </select>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ----------------------------- check-in panel -----------------------------

function CheckInPanel({ occurrenceId }: { occurrenceId: string }) {
  const [search, setSearch] = useState('')
  const [result, setResult] = useState<CheckInResult | null>(null)
  const peopleQuery = useQuery({ queryKey: ['checkin-people'], queryFn: listPeople })

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase()
    const all = peopleQuery.data ?? []
    if (!q) return all.slice(0, 8)
    return all
      .filter((p) => `${p.firstName} ${p.lastName} ${p.email ?? ''}`.toLowerCase().includes(q))
      .slice(0, 8)
  }, [search, peopleQuery.data])

  const doCheckIn = useMutation({
    mutationFn: (personId: string) => checkIn({ occurrenceId, personId }),
    onSuccess: (res) => {
      setResult(res)
      setSearch('')
    },
  })

  if (result) {
    return (
      <Card className="border-success/40">
        <CardContent className="space-y-4 py-6 text-center">
          <CheckCircle2 className="mx-auto size-12 text-success" />
          <div>
            <div className="font-display text-xl font-semibold">
              {result.checkIn.personName ?? 'Checked in'}
            </div>
            <p className="text-sm text-muted">Checked in successfully.</p>
          </div>
          <div className="mx-auto inline-flex flex-col items-center rounded-lg bg-card px-6 py-4">
            <span className="text-xs uppercase tracking-wider text-dim">Pickup code</span>
            <span className="font-mono text-3xl font-bold tracking-[0.3em] text-secondary">
              {result.securityCode}
            </span>
          </div>
          {result.checkIn.isMinor && (
            <div className="mx-auto flex max-w-sm items-center justify-center gap-2 rounded-md bg-warning/10 px-3 py-2 text-xs text-warning">
              <ShieldCheck className="size-4" />
              Guardian pickup required — keep the code tag with the parent.
            </div>
          )}
          <div className="flex items-center justify-center gap-2">
            <Button variant="secondary" onClick={() => window.print()}>
              <Printer /> Print tags ({result.labels.length})
            </Button>
            <Button onClick={() => setResult(null)}>Check in another</Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Check in</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-dim" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            className="pl-9"
            autoFocus
          />
        </div>
        {doCheckIn.isError && (
          <p className="flex items-center gap-1.5 text-sm text-danger">
            <AlertTriangle className="size-4" />
            {doCheckIn.error instanceof Error ? doCheckIn.error.message : 'Check-in failed'}
          </p>
        )}
        <ul className="divide-y divide-border-soft">
          {peopleQuery.isLoading ? (
            <li className="flex items-center gap-2 py-4 text-sm text-muted">
              <Spinner /> Loading directory…
            </li>
          ) : matches.length === 0 ? (
            <li className="py-4 text-center text-sm text-muted">No matching people.</li>
          ) : (
            matches.map((p: Person) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2.5">
                <div>
                  <div className="text-sm font-medium">
                    {p.firstName} {p.lastName}
                  </div>
                  {p.email && <div className="text-xs text-dim">{p.email}</div>}
                </div>
                <Button
                  size="sm"
                  disabled={doCheckIn.isPending}
                  onClick={() => doCheckIn.mutate(p.id)}
                >
                  <LogIn /> Check in
                </Button>
              </li>
            ))
          )}
        </ul>
      </CardContent>
    </Card>
  )
}

// ----------------------------- check-out panel -----------------------------

function CheckOutPanel() {
  const [code, setCode] = useState('')
  const [collectorId, setCollectorId] = useState('')
  const [override, setOverride] = useState(false)
  const [reason, setReason] = useState('')
  const [done, setDone] = useState<string | null>(null)

  const peopleQuery = useQuery({ queryKey: ['checkin-people'], queryFn: listPeople })

  const doCheckOut = useMutation({
    mutationFn: () =>
      checkOut({
        securityCode: code,
        checkedOutByPersonId: collectorId,
        override: override || undefined,
        overrideReason: override ? reason : undefined,
      }),
    onSuccess: (res) => {
      setDone(res.personName ?? 'Checked out')
      setCode('')
      setCollectorId('')
      setOverride(false)
      setReason('')
    },
  })

  if (done) {
    return (
      <Card className="border-success/40">
        <CardContent className="space-y-3 py-6 text-center">
          <CheckCircle2 className="mx-auto size-12 text-success" />
          <div className="font-display text-xl font-semibold">{done}</div>
          <p className="text-sm text-muted">Checked out successfully.</p>
          <Button onClick={() => setDone(null)}>Check out another</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Check out</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted">Pickup code</label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. 7K2M9PQR3T"
            className="font-mono tracking-widest"
            autoFocus
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted">Collecting person</label>
          <select
            value={collectorId}
            onChange={(e) => setCollectorId(e.target.value)}
            className="h-10 w-full rounded-md border border-border bg-bg-alt px-3 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none"
          >
            <option value="">Select who is picking up…</option>
            {(peopleQuery.data ?? []).map((p: Person) => (
              <option key={p.id} value={p.id}>
                {p.firstName} {p.lastName}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-muted">
          <input
            type="checkbox"
            checked={override}
            onChange={(e) => setOverride(e.target.checked)}
            className="size-4 rounded border-border"
          />
          Override guardian check (audited)
        </label>
        {override && (
          <div>
            <label className="mb-1 block text-xs font-medium text-warning">Override reason</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Required — who authorized this pickup?"
              aria-invalid={override && reason.trim().length === 0}
            />
          </div>
        )}
        {doCheckOut.isError && (
          <p className="flex items-center gap-1.5 text-sm text-danger">
            <AlertTriangle className="size-4" />
            {doCheckOut.error instanceof Error ? doCheckOut.error.message : 'Check-out failed'}
          </p>
        )}
        <Button
          className="w-full"
          disabled={
            doCheckOut.isPending || !code.trim() || !collectorId || (override && !reason.trim())
          }
          onClick={() => doCheckOut.mutate()}
        >
          <LogOut /> Check out
        </Button>
      </CardContent>
    </Card>
  )
}

// ----------------------------- page -----------------------------

export function CheckinKiosk() {
  const [mode, setMode] = useState<'in' | 'out'>('in')
  const [occurrenceId, setOccurrenceId] = useState<string | null>(null)

  return (
    <div className="space-y-6">
      <Link
        to="/app/checkins"
        className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-fg"
      >
        <ArrowLeft className="size-4" /> Check-in
      </Link>

      <PageHeader
        title="Station"
        description="Check children and attendees in and out at the kiosk."
        actions={
          <div className="inline-flex rounded-md border border-border p-0.5">
            <Button
              variant={mode === 'in' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setMode('in')}
            >
              <LogIn /> Check in
            </Button>
            <Button
              variant={mode === 'out' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setMode('out')}
            >
              <LogOut /> Check out
            </Button>
          </div>
        }
      />

      {mode === 'in' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <OccurrencePicker value={occurrenceId} onChange={(id) => setOccurrenceId(id || null)} />
          {occurrenceId ? (
            <CheckInPanel occurrenceId={occurrenceId} />
          ) : (
            <Card className="flex items-center justify-center py-16 text-center text-sm text-muted">
              <span>Select a session to begin checking people in.</span>
            </Card>
          )}
        </div>
      ) : (
        <div className="mx-auto max-w-lg">
          <CheckOutPanel />
        </div>
      )}

      <p className="flex items-center justify-center gap-1.5 text-center text-xs text-dim">
        <Badge variant="neutral">Child safety</Badge>
        Minors require an authorized guardian at pickup; overrides are logged.
      </p>
    </div>
  )
}
